import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import CommandPalette from '@/components/CommandPalette';
import Breadcrumbs from '@/components/Breadcrumbs';
import {
  LayoutDashboard,
  Store,
  FolderTree,
  Package,
  ShoppingCart,
  CreditCard,
  Wallet,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Truck,
  ImageIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Management',
    items: [
      { path: '/stores',     label: 'Stores',      icon: Store },
      { path: '/categories', label: 'Categories',  icon: FolderTree },
      { path: '/products',   label: 'Products',    icon: Package },
      { path: '/banners',    label: 'Banners',     icon: ImageIcon },
      { path: '/orders',     label: 'Orders',      icon: ShoppingCart },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/payments',    label: 'Payments',    icon: CreditCard },
      { path: '/wallet-dues', label: 'Wallet Dues', icon: Wallet },
    ],
  },
  {
    label: 'Insights',
    items: [
      { path: '/reports',  label: 'Reports',  icon: BarChart3 },
      { path: '/delivery-settings', label: 'Delivery Settings', icon: Truck },
      { path: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

// Flatten for page-title lookup
const allNavItems = navGroups.flatMap(g => g.items);

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications] = useState([]);
  const [unreadCount] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Cmd+K / Ctrl+K to open command palette
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setCmdOpen(o => !o);
    }
    if (e.key === 'Escape') setCmdOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const getCurrentPageTitle = () => {
    const item = allNavItems.find(
      item => location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
    );
    return item?.label || 'Dashboard';
  };

  const initials = admin?.name
    ? admin.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'A';

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ══════════════════════════════════════
          SIDEBAR
      ══════════════════════════════════════ */}
      <aside
        className={`
          fixed lg:sticky top-0 z-50
          w-[280px] lg:w-64 h-screen flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          shadow-2xl lg:shadow-none
        `}
        style={{ background: 'linear-gradient(180deg, #0f0a28 0%, #1a1040 50%, #120e35 100%)' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white p-1.5 shadow-glow-sm flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/dtx-distributor-logo.png`}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className="font-heading font-bold text-white text-[15px] tracking-tight">DTX Distributor</span>
              <p className="text-white/40 text-[9px] leading-none mt-0.5">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-white/10 rounded-xl text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-3">
          {navGroups.map((group) => (
            <div key={group.label}>
              <p className="sidebar-section-label">{group.label}</p>
              <nav className="space-y-0.5 px-0">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`sidebar-link ${isActive ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="sidebar-icon" />
                      <span className="flex-1">{item.label}</span>
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          ))}
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-default">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[13px] font-medium truncate leading-tight">{admin?.name || 'Admin'}</p>
              <p className="text-white/40 text-[10px] truncate leading-tight mt-0.5">{admin?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* ══════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════ */}
      <div className="flex-1 flex flex-col h-screen min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-100 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2.5 hover:bg-slate-100 rounded-xl transition-colors"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <div className="hidden sm:block">
              <h1 className="text-sm font-heading font-bold text-slate-900 leading-tight">
                {getCurrentPageTitle()}
              </h1>
              <p className="text-[10px] text-slate-400 leading-tight">
                DTX Distributor System
              </p>
            </div>
            <div className="sm:hidden">
              <h1 className="text-sm font-heading font-bold text-slate-900">
                {getCurrentPageTitle()}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Command palette trigger */}
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 text-slate-400 text-xs
                         bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl
                         transition-colors cursor-pointer"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Search…</span>
              <kbd className="ml-1 text-[10px] bg-white border border-slate-200 px-1 py-0.5 rounded font-mono text-slate-400">
                ⌘K
              </kbd>
            </button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative p-2.5 hover:bg-slate-100 rounded-xl transition-colors"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-4.5 h-4.5 text-slate-500" style={{ width: '18px', height: '18px' }} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 z-50 rounded-2xl shadow-xl border-slate-100">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50">
                  <span className="font-semibold text-sm text-slate-900">Notifications</span>
                </div>
                <div className="py-8 text-center text-slate-400 text-sm">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                  No notifications yet
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 pl-1 pr-3 py-1.5 hover:bg-slate-100 rounded-xl transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  >
                    {initials}
                  </div>
                  <span className="text-sm font-medium text-slate-700 hidden sm:block">{admin?.name?.split(' ')[0]}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl shadow-xl border-slate-100">
                <div className="px-4 py-3 border-b border-slate-50">
                  <p className="font-semibold text-sm text-slate-900">{admin?.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{admin?.email}</p>
                </div>
                <DropdownMenuItem asChild>
                  <NavLink to="/settings" className="cursor-pointer gap-2" data-testid="settings-link">
                    <Settings className="w-4 h-4 text-slate-500" />
                    Settings
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="text-rose-600 cursor-pointer gap-2 focus:text-rose-600 focus:bg-rose-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto page-enter">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
